import RecipeImage from '../components/RecipeImage';
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Cookies from 'js-cookie';
import {
    Box,
    Button,
    Card,
    CardContent,
    CircularProgress,
    Container,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Grid,
    IconButton,
    Menu,
    MenuItem,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import Chip from '@mui/material/Chip';
import CollectionsBookmarkIcon from '@mui/icons-material/CollectionsBookmark';
import FavoriteIcon from '@mui/icons-material/Favorite';
import PeopleIcon from '@mui/icons-material/People';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import OutdoorGrillIcon from '@mui/icons-material/OutdoorGrill';
import {
    fetchCollections,
    createCollection,
    updateCollection,
    deleteCollection,
    shareCollection,
    unshareCollection,
} from '../utils/collectionsApi';
import { addToSession, getActiveRecipes, isInSession } from '../utils/cookSession';

const base = () => process.env.REACT_APP_BACKEND_ADDRESS;
const authHeaders = () => ({ 'Authorization': 'Bearer ' + Cookies.get('token') });

const StackedImages = ({ recipeIds, allRecipes }) => {
    const images = recipeIds
        .slice(0, 3)
        .map(id => allRecipes[id]?.download_image_url || '/anchovie.jpg');

    if (images.length === 0) return (
        <Box sx={{ height: 150, display: 'flex', alignItems: 'center', justifyContent: 'center', pt: 4 }}>
            <CollectionsBookmarkIcon sx={{ fontSize: 48, color: 'action.disabled' }} />
        </Box>
    );

    const layouts = {
        1: { rotations: [0],           offsets: [0] },
        2: { rotations: [-8, 8],       offsets: [-36, 36] },
        3: { rotations: [-14, 0, 14],  offsets: [-62, 0, 62] },
    };
    const { rotations, offsets } = layouts[images.length];

    return (
        <Box sx={{ height: 150, position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {images.map((src, i) => (
                <RecipeImage
                    key={i}
                    loading="lazy"
                    src={src}
                    sx={{
                        position: 'absolute',
                        width: 90,
                        height: 90,
                        objectFit: 'cover',
                        borderRadius: 1.5,
                        border: '2px solid white',
                        boxShadow: 2,
                        transform: `rotate(${rotations[i]}deg) translateX(${offsets[i]}px)`,
                        zIndex: i + 1,
                    }}
                />
            ))}
        </Box>
    );
};

const CollectionCard = ({ col, isOwner, allRecipes, onNavigate, onOpenMenu }) => {
    const recipesLabel = `${col.recipe_ids.length} ${col.recipe_ids.length === 1 ? 'recipe' : 'recipes'}`;
    const shareLabel = isOwner && col.shared_with?.length > 0
        ? `· ${col.shared_with.length} ${col.shared_with.length === 1 ? 'collaborator' : 'collaborators'}`
        : !isOwner ? `· by ${col.owner_username}` : null;

    return (
        <Card elevation={0} sx={{ borderRadius: 2, height: 240, display: 'flex', flexDirection: 'column', border: '1px solid', borderColor: 'divider', overflow: 'hidden' }}>
            <Box sx={{ cursor: 'pointer', flex: 1, display: 'flex', flexDirection: 'column', '&:hover .card-content': { bgcolor: 'action.hover' } }} onClick={() => onNavigate(col.id)}>
                <StackedImages recipeIds={col.recipe_ids} allRecipes={allRecipes} />
                <CardContent className="card-content" sx={{ flex: 1, pb: '12px !important', overflow: 'hidden' }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
                        {col.is_favorites && <FavoriteIcon fontSize="small" sx={{ color: 'error.main', mt: 0.3, flexShrink: 0 }} />}
                        <Typography variant="subtitle1" sx={{ fontWeight: 700, flex: 1, lineHeight: 1.3 }} noWrap>
                            {col.name}
                        </Typography>
                        {isOwner && (
                            <Tooltip title="Collection options">
                                <IconButton
                                    size="small"
                                    onClick={(e) => { e.stopPropagation(); onOpenMenu(e, col); }}
                                    sx={{ mt: -0.5, mr: -0.5, flexShrink: 0 }}
                                >
                                    <MoreVertIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        )}
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'nowrap' }}>
                        <Typography variant="body2" color="text.secondary" noWrap>
                            {recipesLabel}{shareLabel ? ` ${shareLabel}` : ''}
                        </Typography>
                        {!isOwner && (
                            <Chip
                                label={col.my_permission === 'edit' ? 'Collaborator' : 'View only'}
                                size="small"
                                color={col.my_permission === 'edit' ? 'primary' : 'default'}
                                sx={{ height: 18, fontSize: 10, flexShrink: 0 }}
                            />
                        )}
                    </Box>
                </CardContent>
            </Box>
        </Card>
    );
};

export default function Collections() {
    const navigate = useNavigate();
    const [collections, setCollections] = useState([]);
    const [loading, setLoading] = useState(true);
    const [allUsers, setAllUsers] = useState([]);
    const [allRecipes, setAllRecipes] = useState({});

    const [newColDialog, setNewColDialog] = useState(false);
    const [newColName, setNewColName] = useState('');

    const [renameDialog, setRenameDialog] = useState(null);
    const [renameName, setRenameName] = useState('');
    const [renameDesc, setRenameDesc] = useState('');

    const [shareDialog, setShareDialog] = useState(null);
    const [userSearch, setUserSearch] = useState('');

    const [menuAnchor, setMenuAnchor] = useState(null);
    const [menuCol, setMenuCol] = useState(null);

    const [cookDialog, setCookDialog] = useState(null); // collection to cook
    const [cookChecked, setCookChecked] = useState({}); // recipeId -> bool

    const currentUserId = JSON.parse(localStorage.getItem('user'))?.id;

    const load = useCallback(async () => {
        const data = await fetchCollections();
        setCollections(data);
        setLoading(false);
    }, []);

    useEffect(() => {
        load();
        const cached = JSON.parse(sessionStorage.getItem('recipeList'));
        if (cached) {
            const map = {};
            Object.values(cached.recipes || {}).flat().forEach(r => { map[r.id] = r; });
            setAllRecipes(map);
        } else {
            fetch(`${base()}/recipes`, { headers: authHeaders() })
                .then(r => r.json())
                .then(data => {
                    sessionStorage.setItem('recipeList', JSON.stringify(data));
                    const map = {};
                    Object.values(data.recipes || {}).flat().forEach(r => { map[r.id] = r; });
                    setAllRecipes(map);
                })
                .catch(() => {});
        }
        fetch(`${base()}/users`, { headers: authHeaders() })
            .then(r => r.json())
            .then(data => setAllUsers(data.users || []))
            .catch(() => {});
    }, [load]);

    const handleCreate = async () => {
        if (!newColName.trim()) return;
        await createCollection(newColName.trim());
        setNewColName('');
        setNewColDialog(false);
        load();
    };

    const handleRename = async () => {
        if (!renameName.trim()) return;
        await updateCollection(renameDialog.id, { name: renameName.trim(), description: renameDesc.trim() || null });
        setRenameDialog(null);
        load();
    };

    const handleDelete = async () => {
        await deleteCollection(menuCol.id);
        setMenuAnchor(null);
        setMenuCol(null);
        load();
    };

    const handleShare = (col, userId, permission = 'view') => {
        const user = allUsers.find(u => u.id === userId);
        const optimistic = (prev) => {
            if (!prev) return prev;
            const existing = prev.shared_with.find(s => s.shared_with_user_id === userId);
            const updated_shares = existing
                ? prev.shared_with.map(s => s.shared_with_user_id === userId ? { ...s, permission } : s)
                : [...prev.shared_with, { id: Date.now(), shared_with_user_id: userId, shared_with_username: `${user?.first_name} ${user?.last_name}`, permission }];
            return { ...prev, shared_with: updated_shares };
        };
        setShareDialog(optimistic);
        setCollections(prev => prev.map(c => c.id === col.id ? optimistic(c) : c));
        shareCollection(col.id, userId, permission).catch(() => fetchCollections().then(data => {
            setCollections(data);
            setShareDialog(data.find(c => c.id === col.id) || null);
        }));
    };

    const handleUnshare = (col, userId) => {
        const optimistic = (prev) => prev ? { ...prev, shared_with: prev.shared_with.filter(s => s.shared_with_user_id !== userId) } : prev;
        setShareDialog(optimistic);
        setCollections(prev => prev.map(c => c.id === col.id ? optimistic(c) : c));
        unshareCollection(col.id, userId).catch(() => fetchCollections().then(data => {
            setCollections(data);
            setShareDialog(data.find(c => c.id === col.id) || null);
        }));
    };

    const openCookDialog = (col) => {
        const initial = {};
        col.recipe_ids.forEach(id => { initial[id] = !isInSession(id); });
        setCookChecked(initial);
        setCookDialog(col);
    };

    const handleCookSession = () => {
        const toAdd = cookDialog.recipe_ids
            .filter(id => cookChecked[id] && allRecipes[id])
            .map(id => allRecipes[id]);
        toAdd.forEach(r => addToSession(r));
        setCookDialog(null);
        if (getActiveRecipes().length > 0) navigate('/cook');
    };

    const openMenu = (e, col) => {
        e.stopPropagation();
        setMenuAnchor(e.currentTarget);
        setMenuCol(col);
    };

    const closeMenu = () => {
        setMenuAnchor(null);
        setMenuCol(null);
    };

    const myCollections = collections.filter(c => c.owner_id === currentUserId);
    const sharedWithMe = collections.filter(c => c.owner_id !== currentUserId);

    const filteredUsers = allUsers.filter(u =>
        u.id !== currentUserId &&
        `${u.first_name} ${u.last_name} ${u.username}`.toLowerCase().includes(userSearch.toLowerCase())
    );

    if (loading) return (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 10 }}>
            <CircularProgress />
        </Box>
    );

    return (
        <Container component="main" sx={{ mt: 5, width: '90%', pb: 12 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Typography variant="h5" sx={{ fontWeight: 700, flex: 1 }}>
                    My Collections
                </Typography>
                <Button startIcon={<AddIcon />} variant="outlined" size="small" onClick={() => setNewColDialog(true)}>
                    New Collection
                </Button>
            </Box>

            {myCollections.length === 0 ? (
                <Typography color="text.secondary" sx={{ mb: 4 }}>
                    You have no collections yet. Create one to start saving recipes.
                </Typography>
            ) : (
                <Grid container spacing={2} sx={{ mb: 4 }}>
                    {myCollections.map(col => (
                        <Grid item xs={12} sm={6} md={4} key={col.id}>
                            <CollectionCard col={col} isOwner={true} allRecipes={allRecipes} onNavigate={(id) => navigate(`/collections/${id}`)} onOpenMenu={openMenu} />
                        </Grid>
                    ))}
                </Grid>
            )}

            {sharedWithMe.length > 0 && (
                <>
                    <Divider sx={{ my: 3 }} />
                    <Typography variant="h5" sx={{ fontWeight: 700, mb: 3 }}>
                        Shared with Me
                    </Typography>
                    <Grid container spacing={2}>
                        {sharedWithMe.map(col => (
                            <Grid item xs={12} sm={6} md={4} key={col.id}>
                                <CollectionCard col={col} isOwner={false} allRecipes={allRecipes} onNavigate={(id) => navigate(`/collections/${id}`)} onOpenMenu={openMenu} />
                            </Grid>
                        ))}
                    </Grid>
                </>
            )}

            {/* Card action menu */}
            <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={closeMenu}>
                {menuCol && menuCol.recipe_ids.length > 0 && (
                    <MenuItem onClick={() => { closeMenu(); openCookDialog(menuCol); }}>
                        <OutdoorGrillIcon fontSize="small" sx={{ mr: 1 }} />
                        Cook this collection
                    </MenuItem>
                )}
                {menuCol && !menuCol.is_favorites && (
                    <MenuItem onClick={() => {
                        closeMenu();
                        setRenameDialog(menuCol);
                        setRenameName(menuCol.name);
                        setRenameDesc(menuCol.description || '');
                    }}>Edit</MenuItem>
                )}
                {menuCol && <MenuItem onClick={() => {
                    const col = menuCol;
                    closeMenu();
                    setShareDialog(col);
                }}>Share</MenuItem>}
                {menuCol && !menuCol.is_favorites && (
                    <MenuItem sx={{ color: 'error.main' }} onClick={handleDelete}>Delete</MenuItem>
                )}
            </Menu>

            {/* New collection dialog */}
            <Dialog open={newColDialog} onClose={() => setNewColDialog(false)} fullWidth maxWidth="xs">
                <DialogTitle sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}><CollectionsBookmarkIcon sx={{ color: 'primary.main' }} />New Collection</DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus fullWidth size="small" label="Name" value={newColName}
                        onChange={e => setNewColName(e.target.value)}
                        onKeyDown={e => e.key === 'Enter' && handleCreate()}
                        sx={{ mt: 1 }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setNewColDialog(false)}>Cancel</Button>
                    <Button variant="contained" onClick={handleCreate}>Create</Button>
                </DialogActions>
            </Dialog>

            {/* Edit dialog */}
            <Dialog open={!!renameDialog} onClose={() => setRenameDialog(null)} fullWidth maxWidth="xs">
                <DialogTitle sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}><EditIcon sx={{ color: 'primary.main' }} />Edit Collection</DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus fullWidth size="small" label="Name" value={renameName}
                        onChange={e => setRenameName(e.target.value)}
                        sx={{ mt: 1, mb: 2 }}
                    />
                    <TextField
                        fullWidth size="small" label="Description (optional)" value={renameDesc}
                        onChange={e => setRenameDesc(e.target.value)}
                        onKeyDown={e => e.key === 'Enter' && handleRename()}
                        multiline rows={2}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setRenameDialog(null)}>Cancel</Button>
                    <Button variant="contained" onClick={handleRename}>Save</Button>
                </DialogActions>
            </Dialog>

            {/* Share dialog */}
            <Dialog open={!!shareDialog} onClose={() => { setShareDialog(null); setUserSearch(''); }} fullWidth maxWidth="xs">
                <DialogTitle sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}><PeopleIcon sx={{ color: 'primary.main' }} />Share "{shareDialog?.name}"</DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus fullWidth size="small" placeholder="Search users…" value={userSearch}
                        onChange={e => setUserSearch(e.target.value)}
                        sx={{ mt: 1, mb: 1 }}
                    />
                    {filteredUsers.map(u => {
                        const existingShare = shareDialog?.shared_with?.find(s => s.shared_with_user_id === u.id);
                        return (
                            <Box key={u.id} sx={{ display: 'flex', alignItems: 'center', py: 0.75, gap: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>{u.first_name} {u.last_name}</Typography>
                                    <Typography variant="caption" color="text.secondary">@{u.username}</Typography>
                                </Box>
                                {existingShare ? (
                                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5 }}>
                                        <ToggleButtonGroup
                                            size="small"
                                            exclusive
                                            value={existingShare.permission}
                                            onChange={(_, val) => { if (val) handleShare(shareDialog, u.id, val); }}
                                        >
                                            <ToggleButton value="view" sx={{ py: 0.25, px: 1, fontSize: 11 }}>View</ToggleButton>
                                            <ToggleButton value="edit" sx={{ py: 0.25, px: 1, fontSize: 11 }}>Edit</ToggleButton>
                                        </ToggleButtonGroup>
                                        <Button size="small" color="error" sx={{ py: 0, minWidth: 0 }} onClick={() => handleUnshare(shareDialog, u.id)}>Remove</Button>
                                    </Box>
                                ) : (
                                    <Button size="small" onClick={() => handleShare(shareDialog, u.id, 'view')}>Add</Button>
                                )}
                            </Box>
                        );
                    })}
                    {filteredUsers.length === 0 && (
                        <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>No users found.</Typography>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => { setShareDialog(null); setUserSearch(''); }}>Done</Button>
                </DialogActions>
            </Dialog>
            {/* Cook collection dialog */}
            <Dialog open={!!cookDialog} onClose={() => setCookDialog(null)} fullWidth maxWidth="xs">
                <DialogTitle sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}><OutdoorGrillIcon sx={{ color: 'primary.main' }} />Cook "{cookDialog?.name}"</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        Select which recipes to add to your cooking session.
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Button size="small" sx={{ textTransform: 'none' }} onClick={() => {
                            const all = {};
                            cookDialog.recipe_ids.forEach(id => { all[id] = !isInSession(id); });
                            setCookChecked(all);
                        }}>Select all</Button>
                        <Button size="small" sx={{ textTransform: 'none' }} onClick={() => {
                            const none = {};
                            cookDialog.recipe_ids.forEach(id => { none[id] = false; });
                            setCookChecked(none);
                        }}>Deselect all</Button>
                    </Box>
                    {cookDialog?.recipe_ids.map(id => {
                        const recipe = allRecipes[id];
                        if (!recipe) return null;
                        const alreadyIn = isInSession(id);
                        return (
                            <Box key={id} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5, borderBottom: '1px solid', borderColor: 'divider', opacity: alreadyIn ? 0.5 : 1 }}>
                                <RecipeImage
                                    src={recipe.download_image_url || '/anchovie.jpg'}
                                    alt={recipe.title}
                                    sx={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 1, flexShrink: 0 }}
                                />
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={!!cookChecked[id]}
                                            onChange={e => setCookChecked(prev => ({ ...prev, [id]: e.target.checked }))}
                                            size="small"
                                            disabled={alreadyIn}
                                        />
                                    }
                                    label={
                                        <Box sx={{ minWidth: 0 }}>
                                            <Typography variant="body2" noWrap>{recipe.title}</Typography>
                                            {alreadyIn && <Typography variant="caption" color="text.secondary">Already in session</Typography>}
                                        </Box>
                                    }
                                    sx={{ flex: 1, m: 0, minWidth: 0 }}
                                />
                            </Box>
                        );
                    })}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setCookDialog(null)}>Cancel</Button>
                    <Button
                        variant="contained"
                        startIcon={<OutdoorGrillIcon />}
                        onClick={handleCookSession}
                        disabled={!Object.values(cookChecked).some(Boolean)}
                    >
                        {getActiveRecipes().length > 0 ? 'Add to Session' : 'Start Cooking'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
}
